import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Icon, type IconName } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  WASH_HOVER,
  WASH_SELECTED,
  type SurfaceToken,
} from "../../components/ui/box-classes";
import { cn } from "../../lib/utils";
import {
  EntityAvatar,
  EntityAvatarFallback,
  EntityAvatarImage,
} from "../entity-group";
import * as React from "react";
import { useEntitySelector, type EntityItemConfig } from "./entity-selector-context";
import type { Omittable } from "../../lib/props";

function itemClasses({
  highlighted,
  active,
  disabled,
  spacious,
  surface,
}: {
  highlighted: boolean;
  active: boolean;
  disabled: boolean;
  spacious: boolean;
  surface: SurfaceToken;
}): string {
  // mx-1 + px-1 keeps content at the same 8px reading edge as the section
  // headings and search row while insetting the rounded hover wash 4px from
  // the popover edge, so the wash never runs flush into the border radius.
  return cn(
    "group relative flex flex-row items-center gap-3 rounded-md mx-1 px-1 cursor-pointer",
    // Row rhythm is a property of the LIST, not of the row. A flat list keeps the
    // compact 3px every fixed-height popup was sized against — the assignee panel
    // and the issue menu bound their lists in hard pixels, so a taller row there
    // costs visible members rather than reflowing. A SECTIONED list takes 8px
    // (6px once a pointer is likely, sm+), because 3px reads as cramped beside a
    // heading: the groups stop being legible when the rows inside them sit as
    // close together as the eyebrows above them.
    spacious ? "py-2 sm:py-1.5" : "py-[3px]",
    WASH_HOVER,
    (highlighted || active) && WASH_SELECTED,
    disabled && "opacity-50",
  );
}

function subtitleClasses(hideOnHover: boolean): string {
  return cn(
    "shrink-0 text-xs text-muted-foreground",
    hideOnHover && "group-hover:hidden",
  );
}

// Hover-gated action buttons float over the row's right edge; touch surfaces
// pass `alwaysShowActions` to render them inline instead.
const HOVER_ACTIONS_CLASSES =
  "absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100";

const PADDED_ICON_CLASSES = "p-1 rounded-md";

const ItemActionButton = ({
  label,
  onClick,
  variant,
}: {
  label: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  variant: "outline" | "default";
}) => (
  <Button variant={variant} size="xs" onClick={onClick} tabIndex={-1}>
    {label}
  </Button>
);

const ItemSubtitle = ({
  hideOnHover,
  ...props
}: { hideOnHover?: boolean; children: React.ReactNode } & Omit<
  React.ComponentProps<typeof Text>,
  "className"
>) => <Text className={subtitleClasses(!!hideOnHover)} {...props} />;

export type EntitySelectorItemProps = Omittable<{
  entity: EntityItemConfig;
  poolId: string;
  onAction?: (entity: EntityItemConfig) => void;
  actionLabel?: string;
  secondaryActionLabel?: string;
  onSecondaryAction?: (entity: EntityItemConfig) => void;
  showCheckbox?: boolean;
  hideSelectionHighlight?: boolean;
  /** Optional label shown after the entity title to indicate which pool an item belongs to. */
  poolLabel?: string;
  /** Hide the avatar/icon column. @default false */
  hideAvatar?: boolean;
  /**
   * Roomier vertical rhythm, for a list whose rows sit under section eyebrows.
   * The pool sets it from whether its items carry sections; a flat list keeps the
   * compact rhythm its fixed-height popup was measured against. @default false
   */
  spacious?: boolean;
  /** Render action buttons without hover gating. Use on touch surfaces where hover is unavailable. */
  alwaysShowActions?: boolean;
  /** Whether this is the active (highlighted) item — independent of selection. */
  active?: boolean;
  /** Index of this item within the pool's filtered list. */
  index?: number;
  /** Make this item active (hover and keyboard both drive it). */
  onActivate?: (index: number) => void;
  ref?: React.Ref<HTMLDivElement>;
}>;

function EntitySelectorItemInner({
  entity,
  poolId,
  onAction,
  actionLabel,
  secondaryActionLabel,
  onSecondaryAction,
  showCheckbox = true,
  hideSelectionHighlight = false,
  poolLabel,
  hideAvatar = false,
  spacious = false,
  alwaysShowActions = false,
  active = false,
  index,
  onActivate,
  ref,
}: EntitySelectorItemProps) {
  const { toggleSelection, isSelected, config } = useEntitySelector();
  const selected = isSelected(entity.id);
  const disabled = entity.disabled ?? false;
  const singleSelect = config.maxSelections === 1;
  const multiline = !!config.multilineItems;

  const handleClick = React.useCallback(() => {
    if (disabled) return;
    toggleSelection(entity, poolId);
  }, [disabled, entity, poolId, toggleSelection]);

  const handleAction = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onAction?.(entity);
    },
    [entity, onAction],
  );

  const handleSecondaryAction = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onSecondaryAction?.(entity);
    },
    [entity, onSecondaryAction],
  );

  const hasActions =
    (actionLabel && onAction) || (secondaryActionLabel && onSecondaryAction);

  const handlePointerEnter = React.useCallback(() => {
    if (index != null) onActivate?.(index);
  }, [index, onActivate]);

  const avatarFallback = React.useMemo(() => {
    if (entity.avatar?.fallback) return entity.avatar.fallback;
    const [firstWord, secondWord] = entity.title
      .split(" ")
      .filter((w) => w.length > 0);
    if (firstWord && secondWord) {
      return `${firstWord.slice(0, 1)}${secondWord.slice(0, 1)}`.toUpperCase();
    }
    return entity.title.trim().slice(0, 2).toUpperCase() || "??";
  }, [entity.avatar?.fallback, entity.title]);

  const avatarIcon = entity.avatar?.icon ?? "user";
  const avatarColor = entity.avatar?.color;

  const row = (
    <Box
      ref={ref}
      direction="row"
      onClick={disabled ? undefined : handleClick}
      onPointerEnter={onActivate ? handlePointerEnter : undefined}
      id={`entity-item-${entity.id}`}
      tabIndex={-1}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      data-entity-id={entity.id}
      className={cn(
        itemClasses({
          highlighted: selected && !hideSelectionHighlight,
          active,
          disabled,
          spacious,
          surface: config.surface ?? "popover",
        }),
      )}
    >
      {!singleSelect && showCheckbox && (
        <Box pointerEvents="none">
          <Checkbox
            checked={selected}
            onCheckedChange={() => toggleSelection(entity, poolId)}
            disabled={disabled}
          />
        </Box>
      )}

      {!hideAvatar &&
        (entity.avatar?.render ? (
          entity.avatar.render()
        ) : entity.avatar?.padded && entity.avatar?.icon ? (
          <Box
            data-color={avatarColor}
            className={cn(
              PADDED_ICON_CLASSES,
              avatarColor ? "bg-c-soft" : "bg-muted/15",
            )}
          >
            <Icon
              {...(typeof avatarIcon === "string"
                ? { name: avatarIcon as IconName }
                : { as: avatarIcon })}
              size="md"
              color={avatarColor}
            />
          </Box>
        ) : (
          <EntityAvatar
            size="sm"
            alt={entity.title}
            muted={entity.muted}
            invited={entity.invited}
          >
            {entity.avatar?.src ? (
              <EntityAvatarImage alt={entity.title} src={entity.avatar.src} />
            ) : null}
            <EntityAvatarFallback
              // The colour is a NAME, so it travels as data and the cascade
              // does the lookup — same as the padded branch above. Writing it
              // to `backgroundColor` painted the CSS keyword that happened to
              // share the name (`blue`) and nothing at all for the ones that
              // do not (`primary`, `light-purple`).
              data-color={avatarColor}
              className={cn(
                avatarColor
                  ? "bg-c text-c-foreground"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {entity.avatar?.icon ? (
                <Icon
                  {...(typeof avatarIcon === "string"
                    ? { name: avatarIcon as IconName }
                    : { as: avatarIcon })}
                  size="lg"
                  color="muted"
                />
              ) : (
                <Text size="xs" weight="medium" variant="muted">
                  {avatarFallback}
                </Text>
              )}
            </EntityAvatarFallback>
          </EntityAvatar>
        ))}

      {multiline ? (
        <Box direction="column" flex={1} minWidth={0} gap={1}>
          <ItemTexts
            entity={entity}
            multiline
            hasActions={!!hasActions}
            alwaysShowActions={alwaysShowActions}
            poolLabel={poolLabel}
          />
        </Box>
      ) : (
        <Box direction="row" align="baseline" flex={1} minWidth={0} gap="sm">
          <ItemTexts
            entity={entity}
            multiline={false}
            hasActions={!!hasActions}
            alwaysShowActions={alwaysShowActions}
            poolLabel={poolLabel}
          />
        </Box>
      )}

      {singleSelect && selected && (
        <Box shrink={false}>
          <Icon name="check" size="lg" color="primary" />
        </Box>
      )}

      {hasActions && (
        <Box
          direction="row"
          align="center"
          gap="xs"
          className={cn(!alwaysShowActions && HOVER_ACTIONS_CLASSES)}
        >
          {secondaryActionLabel && onSecondaryAction && (
            <ItemActionButton
              label={secondaryActionLabel}
              onClick={handleSecondaryAction}
              variant="default"
            />
          )}
          {actionLabel && onAction && (
            <ItemActionButton
              label={actionLabel}
              onClick={handleAction}
              variant="outline"
            />
          )}
        </Box>
      )}
    </Box>
  );

  if (!entity.tooltip) return row;
  return (
    <Tooltip>
      <TooltipTrigger render={row} />
      <TooltipPopup side="right">
        <Text size="xs">{entity.tooltip}</Text>
      </TooltipPopup>
    </Tooltip>
  );
}

function ItemTexts({
  entity,
  multiline,
  hasActions,
  alwaysShowActions,
  poolLabel,
}: Omittable<{
  entity: EntityItemConfig;
  multiline: boolean;
  hasActions: boolean;
  alwaysShowActions: boolean;
  poolLabel?: string;
}>) {
  return (
    <>
      <Text
        size="sm"
        weight="medium"
        truncate
        variant={entity.muted ? "muted" : undefined}
        className="min-w-0 shrink"
      >
        {entity.title}
      </Text>
      {!!entity.description && (
        <ItemSubtitle
          hideOnHover={!multiline && !!(hasActions && !alwaysShowActions)}
          truncate={multiline ? 2 : undefined}
        >
          {entity.description}
        </ItemSubtitle>
      )}
      {!!poolLabel && <ItemSubtitle truncate>{poolLabel}</ItemSubtitle>}
    </>
  );
}

EntitySelectorItemInner.displayName = "EntitySelectorItemInner";

const EntitySelectorItem = React.memo(EntitySelectorItemInner, (prev, next) => {
  return (
    prev.entity.id === next.entity.id &&
    prev.entity.title === next.entity.title &&
    prev.entity.description === next.entity.description &&
    prev.entity.tooltip === next.entity.tooltip &&
    prev.entity.disabled === next.entity.disabled &&
    prev.entity.muted === next.entity.muted &&
    prev.entity.invited === next.entity.invited &&
    prev.entity.avatar?.src === next.entity.avatar?.src &&
    prev.entity.avatar?.fallback === next.entity.avatar?.fallback &&
    prev.entity.avatar?.icon === next.entity.avatar?.icon &&
    prev.entity.avatar?.color === next.entity.avatar?.color &&
    prev.entity.avatar?.padded === next.entity.avatar?.padded &&
    prev.entity.avatar?.render === next.entity.avatar?.render &&
    prev.poolId === next.poolId &&
    prev.actionLabel === next.actionLabel &&
    prev.secondaryActionLabel === next.secondaryActionLabel &&
    prev.showCheckbox === next.showCheckbox &&
    prev.hideSelectionHighlight === next.hideSelectionHighlight &&
    prev.onAction === next.onAction &&
    prev.onSecondaryAction === next.onSecondaryAction &&
    prev.poolLabel === next.poolLabel &&
    prev.hideAvatar === next.hideAvatar &&
    prev.spacious === next.spacious &&
    prev.alwaysShowActions === next.alwaysShowActions &&
    prev.active === next.active &&
    prev.index === next.index &&
    prev.onActivate === next.onActivate
  );
});

EntitySelectorItem.displayName = "EntitySelectorItem";

export { EntitySelectorItem };
